/**
 * MetricsGrid - every counter the summary carries, for people who want the
 * whole board rather than the four headline figures.
 *
 * Same treatment as `QuickMetrics`, one level up in density: a single
 * `GlassCard` holding a divided grid of `KpiTile`s, sized against the WIDGET
 * through the `.widget-metrics-*` container queries so a half-width card gets
 * fewer columns than a full-width one on the same screen.
 *
 * The nine per-metric colour ramps are gone (violet, pink, cyan, slate, sky,
 * teal - six of the nine were not brand tokens at all). `tone` survives on the
 * two counters where colour is information rather than decoration: overdue
 * tasks in `danger` when there are any, completed tasks in `success`.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardOverview } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import { METRIC_BY_KEY, MetricKey } from './metricCatalog';
import { PiSquaresFourLight } from 'react-icons/pi';

interface MetricsGridProps {
  overview: DashboardOverview | null;
}

/*
  The board's order. The tiles themselves - label, hint, icon, value, tone,
  route - come from `metricCatalog`, the ONE list At a glance uses too; this
  card used to carry its own copy, which had drifted ("Clients", "In the
  vault", a hint that didn't match what was counted).
*/
const GRID_ORDER: MetricKey[] = [
  'projects', 'projectResources', 'matters', 'tasksOpen', 'tasksOverdue',
  'tasksCompleted', 'reviews', 'clients', 'tables', 'files',
];

const MetricsGrid: React.FC<MetricsGridProps> = ({ overview }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  if (!overview) return null;

  const metrics = GRID_ORDER.map((key) => METRIC_BY_KEY[key]).filter(Boolean).map((m) => ({
    key: m.key,
    title: t(m.label),
    value: m.value(overview),
    hint: t(m.hint),
    icon: m.icon,
    tone: m.tone?.(overview),
    section: m.section,
    route: m.route,
  }));

  const visibleMetrics = metrics.filter((m) => !m.section || hasSectionAccess(m.section));

  if (visibleMetrics.length === 0) return null;

  const gridCols = clsx(
    'widget-metrics',
    visibleMetrics.length <= 4 && 'widget-metrics-4',
    visibleMetrics.length > 4 && visibleMetrics.length <= 6 && 'widget-metrics-6',
    visibleMetrics.length > 6 && 'widget-metrics-8'
  );

  return (
    <GlassCard
      icon={<PiSquaresFourLight />}
      title={t('All metrics')}
      padding="none"
      className="overflow-hidden"
    >
      <div className={clsx(gridCols, 'divide-x divide-y divide-hairline-soft')}>
        {visibleMetrics.map((metric) => (
          <button
            key={metric.key}
            type="button"
            onClick={() => history.push(metric.route)}
            className="text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/60 focus:bg-surface-sunken/60 focus:outline-none"
          >
            <KpiTile
              icon={metric.icon}
              value={metric.value.toLocaleString()}
              label={metric.title}
              hint={metric.hint}
              tone={metric.tone}
            />
          </button>
        ))}
      </div>
    </GlassCard>
  );
};

export default MetricsGrid;
